import React, { useState, useEffect } from 'react'
import { Grid, Container, Box, Button, TextField, FormControl, InputLabel, Select, MenuItem, Typography, Alert } from '@mui/material'
import { getDefaults, saveDefaults } from './util'
import { type GetDefaultsResponse } from './model'

const models = [
  'gemini-1.0-pro-001',
  'text-bison',
  'text-bison-32k',
  'chat-bison',
  'chat-bison-32k',
  'gpt-4',
  'gpt-4-32k',
  'gpt-4-1106-preview',
  'gpt-3.5-turbo-1106',
  'gpt-3.5-turbo',
  'gpt-3.5-turbo-16k',
  'gpt-3.5-turbo-instruct',
  'gpt-3.5',
  'gpt-4o',
]

const SettingsForm: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('') // add error state
  const [hasError, setHasError] = useState(false) // add error state
  const [hasSoftError, setSoftError] = useState(false) // add error state
  const [model, setModel] = useState('')
  const [translationPromptMessage, setTranslationPromptMessage] = useState('')
  const [grammarCheckFormat, setGrammarCheckFormat] = useState('')
  const [systemMessage, setSystemMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('') // add success state
  const [firstBotMessage, setFirstBotMessage] = useState('')

  useEffect(() => {
    getDefaults()
      .then(data => {
        setModel(data.model)
        setTranslationPromptMessage(data.translationMessage)
        setSystemMessage(data.systemMessage)
        setGrammarCheckFormat(data.grammarCheckFormat)
        setFirstBotMessage(data.firstBotMessage)
        setLoading(false)
      }).catch(err => {
        setErrorMessage(err.toString() as string)
        setHasError(true)
        setLoading(false)
      })
  }, [])

  if (loading) {
    return <div>Loading...</div> // render loading message while defaults are being loaded
  }
  if (hasError) {
    return <Alert severity="error">{errorMessage}</Alert> // render error banner if there's an error
  }

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    const newDefaults = {
      model,
      translationMessage: translationPromptMessage,
      systemMessage,
      grammarCheckFormat,
      firstBotMessage
    }

    saveDefaults(newDefaults)
      .then(() => {
        setSuccessMessage('Defaults saved successfully!') // set success state if saveDefaults succeeds
        setSoftError(false)
      })
      .catch(err => {
        setErrorMessage('Failed to save defaults: ' + err) // set error state if saveDefaults fails
        setSoftError(true)
      })
  }

  // Presets

  const frenchPreset = {
    model: 'gpt-3.5-turbo',
    translationMessage: 'You are a French-to-English dictionary API. You return the definition in JSON format (with quoted field names) with the following format: {translations: [{word: string, definition: string, type: string}]}',
    systemMessage: 'My French-speaking AI tutor.',
    grammarCheckFormat: 'User Sentence: %s\nPlease analyze the sentence for any grammar errors and provide feedback.',
    firstBotMessage: ''
  }

  const spanishPreset = {
    model: 'chat-bison',
    translationMessage: 'You are a Spanish-to-English dictionary API. You return the definition in JSON format (with quoted field names) with the following format: {translations: [{word: string, definition: string, type: string}]}',
    systemMessage: "Imagine you are my friendly companion that's talking causally to me ok WhatsApp. Let's have a fun conversation in Spanish! You start!'",
    grammarCheckFormat: 'User Sentence: %s\nPlease analyze the sentence for any grammar errors and provide feedback.',
    firstBotMessage: ''
  }

  const handlePreset = (preset: GetDefaultsResponse): void => {
    setModel(preset.model)
    setTranslationPromptMessage(preset.translationMessage)
    setSystemMessage(preset.systemMessage)
    setGrammarCheckFormat(preset.grammarCheckFormat)
  }

  return (
    <Container>
      {hasSoftError && <Alert severity="error">{errorMessage}</Alert>} {/* render success message if there's a success */}
      {successMessage !== ' ' && <Alert severity="success">{successMessage}</Alert>} {/* render success message if there's a success */}
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={8} md={6}>
          <Typography variant="h2">Settings</Typography>
        </Grid>
        <Grid item xs={12} sm={8} md={6}>
          <form onSubmit={handleSubmit}>
            <Box marginBottom={2}>
              <FormControl fullWidth>
                <InputLabel>Models</InputLabel>
                <Select
                  value={model}
                  onChange={(e) => { setModel(e.target.value) }}
                >
                  {models.map((model) => (
                    <MenuItem key={model} value={model}>
                      {model}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box marginBottom={2}>
              <TextField
                fullWidth
                multiline
                label="Translation Prompt Message"
                value={translationPromptMessage}
                onChange={(e) => { setTranslationPromptMessage(e.target.value) }}
              />
            </Box>
            <Box marginBottom={2}>
              <TextField
                fullWidth
                multiline
                label="Grammar check format"
                value={grammarCheckFormat}
                onChange={(e) => { setGrammarCheckFormat(e.target.value) }}
              />
            </Box>
            <Box marginBottom={2}>
              <TextField
                fullWidth
                multiline
                label="System Message"
                value={systemMessage}
                onChange={(e) => { setSystemMessage(e.target.value) }}
              />
            </Box>
            <Box marginBottom={2}>
              <TextField
                fullWidth
                multiline
                label="First Bot Message"
                value={firstBotMessage}
                onChange={(e) => { setFirstBotMessage(e.target.value) }}
              />
            </Box>
            <Button type="submit">Save</Button>
          </form>
        </Grid>
        <Grid item xs={12} sm={8} md={6}>
          <Button onClick={() => { handlePreset(frenchPreset) }}>Load French Preset</Button>
          <Button onClick={() => { handlePreset(spanishPreset) }}>Load Spanish Preset</Button>
        </Grid>
      </Grid>
    </Container>
  )
}

export default SettingsForm
// generate a mui form with the following fields: timeout (in secods), models (as a dropdown), and a submit button, translation prompt message, system message
